import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Star, Upload, Send, MapPin, X, LogIn, LogOut, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local review images - bundled with the build, always available
const REVIEW_IMAGES = {
  "Michael Hughes": "/images/reviews/review_windows1.jpg",
  "Karen Stewart": "/images/reviews/review_door1.jpg",
  "Robert Anderson": "/images/reviews/review_windows2.jpg",
  "Sandra & John Campbell": "/images/reviews/review_casement.jpg",
  "David McPherson": "/images/reviews/review_garden.jpg",
  "Margaret Thomson": "/images/reviews/review_door2.jpg",
  "James Wilson": "/images/reviews/review_door3.jpg",
};

const getReviewImage = (review) => {
  // Use local image if this is a pre-seeded review
  if (REVIEW_IMAGES[review.customer_name]) {
    return REVIEW_IMAGES[review.customer_name];
  }
  // Skip old broken URLs from previous deployment
  if (review.image_url && review.image_url.includes("emergent.host/api/uploads/")) {
    return "";
  }
  return review.image_url || "";
};

const ReviewsPage = () => {
  const location = useLocation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Google Auth state
  const [user, setUser] = useState(location.state?.user || null);
  const [checkingAuth, setCheckingAuth] = useState(!location.state?.user);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    location: "",
    rating: 5,
    review_text: "",
    service_type: "",
    image_url: ""
  });

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  }, []);

  // Check auth status on mount
  useEffect(() => {
    // If user was passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setCheckingAuth(false);
      // Show success toast if just logged in
      if (location.state?.justLoggedIn) {
        toast.success(`Welcome, ${location.state.user.name}! You can now submit a review.`);
        setShowForm(true);
        // Pre-fill name from Google account
        setFormData(prev => ({ ...prev, customer_name: location.state.user.name }));
      }
      return;
    }

    // Check if user is already logged in via cookie
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        // Pre-fill name from Google account
        setFormData(prev => ({ ...prev, customer_name: response.data.name }));
      } catch {
        // Not logged in - that's fine
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [location.state]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Handle auth error from callback
  useEffect(() => {
    if (location.state?.authError) {
      toast.error(location.state.authError);
    }
  }, [location.state?.authError]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/reviews';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setFormData(prev => ({ ...prev, customer_name: "" }));
      toast.success("Signed out successfully");
    } catch {
      toast.error("Sign out failed");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, WebP, or GIF)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/review`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Build full URL for the image
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      setImagePreview(URL.createObjectURL(file));
      toast.success("Image uploaded successfully");
    } catch {
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: "" }));
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require Google sign-in
    if (!user) {
      toast.error("Please sign in with Google to submit a review");
      return;
    }
    
    setSubmitting(true);

    try {
      await axios.post(`${API}/reviews`, formData, { withCredentials: true });
      toast.success("Thank you! Your review has been submitted for approval.");
      setFormData({
        customer_name: user?.name || "",
        location: "",
        rating: 5,
        review_text: "",
        service_type: "",
        image_url: ""
      });
      setImagePreview(null);
      setShowForm(false);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Please sign in with Google to submit a review");
        setUser(null);
      } else {
        toast.error("Failed to submit review. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, size = "w-5 h-5") => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : undefined}
            onClick={interactive ? () => handleRatingClick(star) : undefined}
            className={interactive ? "cursor-pointer" : "cursor-default"}
            disabled={!interactive}
          >
            <Star
              className={`${size} ${
                star <= rating
                  ? "fill-[#FF6B00] text-[#FF6B00]"
                  : "fill-gray-200 text-gray-200"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="reviews-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Customer Reviews | Double Glazing Motherwell, Hamilton, Glasgow, Lanarkshire"
        description="Read customer reviews for C. Glancey Double Glazing. Trusted for uPVC windows, composite doors & garden rooms in Motherwell, Hamilton, Glasgow & Lanarkshire. See our 5-star rated work."
        keywords="customer reviews, double glazing reviews, window installation reviews Lanarkshire, double glazing reviews Motherwell, composite doors reviews Glasgow, C Glancey reviews, testimonials"
        canonical="/reviews"
      />

      {/* Hero Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            CUSTOMER <span className="text-[#FF6B00]">REVIEWS</span>
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">
            Read customer reviews of our <Link to="/windows" className="text-[#FF6B00] hover:underline">double glazing windows</Link>, <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link>, and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link>. We're proud of every installation
            and love hearing from satisfied customers across Lanarkshire.
          </p>
          
          {/* User status and action buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {checkingAuth ? (
              <div className="text-gray-400">Checking sign-in status...</div>
            ) : user ? (
              <>
                {/* Logged in user info */}
                <div className="flex items-center gap-2 text-white bg-[#1A1A1A] px-4 py-2 rounded-full">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-5 h-5" />
                  )}
                  <span className="text-sm">{user.name}</span>
                </div>
                <button
                  onClick={() => setShowForm(!showForm)}
                  data-testid="write-review-btn"
                  className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all"
                >
                  {showForm ? "Close Form" : "Write a Review"}
                </button>
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                  className="inline-flex items-center gap-3 bg-white text-[#0A0A0A] font-bold px-6 py-4 hover:bg-gray-100 transition-all rounded-lg shadow-lg"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google to Write a Review
                </button>
                <p className="text-gray-500 text-sm">Verified reviews from real customers</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Review Form */}
      {showForm && user && (
        <section className="py-12 bg-[#F5F5F5]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <form onSubmit={handleSubmit} className="bg-white p-8 shadow-lg">
              <h2 
                className="text-2xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                SHARE YOUR <span className="text-[#FF6B00]">EXPERIENCE</span>
              </h2>

              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    data-testid="review-name"
                    className="w-full border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">
                    Your Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    data-testid="review-location"
                    className="w-full border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors"
                    placeholder="Motherwell"
                  />
                </div>

                {/* Service Type */}
                <div>
                  <label className="block text-sm font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">
                    Service Received *
                  </label>
                  <select
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    required
                    data-testid="review-service"
                    className="w-full border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors bg-white"
                  >
                    <option value="">Select a service</option>
                    <option value="Windows">Windows</option>
                    <option value="Doors">Doors</option>
                    <option value="Garden Room">Garden Room</option>
                    <option value="Glazing Repairs">Glazing Repairs</option>
                    <option value="Full House">Full House Installation</option>
                  </select>
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-sm font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">
                    Your Rating *
                  </label>
                  <div data-testid="review-rating">
                    {renderStars(formData.rating, true, "w-8 h-8")}
                  </div>
                </div>

                {/* Review Text */}
                <div>
                  <label className="block text-sm font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">
                    Your Review *
                  </label>
                  <textarea
                    name="review_text"
                    value={formData.review_text}
                    onChange={handleChange}
                    required
                    rows={4}
                    data-testid="review-text"
                    className="w-full border-b-2 border-[#E5E5E5] focus:border-[#FF6B00] px-4 py-3 outline-none transition-colors resize-none"
                    placeholder="Tell us about your experience..."
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-bold text-[#0A0A0A] uppercase tracking-wide mb-2">
                    Add a Photo (Optional)
                  </label>
                  {!imagePreview ? (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#E5E5E5] cursor-pointer hover:border-[#FF6B00] transition-colors">
                      <Upload className="w-8 h-8 text-[#525252] mb-2" />
                      <span className="text-sm text-[#525252]">
                        {uploadingImage ? "Uploading..." : "Click to upload image"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                  ) : (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="max-h-40 rounded"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-review-btn"
                  className="w-full bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Review
                    </>
                  )}
                </button>

                <p className="text-xs text-[#525252] text-center">
                  Your review will be visible after approval by our team.
                </p>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* Reviews Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4" />
              <p className="text-[#525252]">Loading reviews...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#525252] text-lg mb-4">No reviews yet.</p>
              <p className="text-[#525252]">Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  data-testid={`review-card-${review.id}`}
                  className="bg-[#F5F5F5] p-6 border-l-4 border-[#FF6B00]"
                >
                  {/* Image */}
                  {getReviewImage(review) && (
                    <div className="mb-4 overflow-hidden">
                      <img
                        src={getReviewImage(review)}
                        alt={`Review by ${review.customer_name}`}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}

                  {/* Rating */}
                  <div className="mb-3">
                    {renderStars(review.rating)}
                  </div>

                  {/* Review Text */}
                  <p className="text-[#525252] mb-4 italic">
                    "{review.review_text}"
                  </p>

                  {/* Customer Info */}
                  <div className="border-t border-[#E5E5E5] pt-4">
                    <p className="font-bold text-[#0A0A0A]">{review.customer_name}</p>
                    <div className="flex items-center gap-1 text-sm text-[#525252]">
                      <MapPin className="w-4 h-4 text-[#FF6B00]" />
                      <span>{review.location}</span>
                    </div>
                    <span className="inline-block mt-2 text-xs bg-[#FF6B00] text-white px-2 py-1 uppercase tracking-wide">
                      {review.service_type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-2xl sm:text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            READY TO JOIN OUR <span className="text-[#FF6B00]">HAPPY CUSTOMERS?</span>
          </h2>
          <p className="text-gray-300 mb-8">
            Get in touch today for a free, no-obligation quote on <Link to="/windows" className="text-[#FF6B00] hover:underline">double glazing windows</Link>, <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link>, or <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link>.
          </p>
          <a
            href="tel:07917181597"
            className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all"
          >
            Call 07917 181597
          </a>
        </div>
      </section>

      {/* Related Services Links */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Explore Our Services</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/windows" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Double Glazing Windows
            </Link>
            <Link to="/doors" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Composite & uPVC Doors
            </Link>
            <Link to="/outbuilds" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Garden Rooms
            </Link>
            <Link to="/recent-work" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              View Our Recent Work
            </Link>
            <Link to="/faq" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Window Installation FAQ
            </Link>
            <Link to="/contact" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors text-sm font-medium">
              Get a Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
